/**
 * Espoto keyword replacement ("Storytelling Booster") — balanced-bracket parser.
 *
 * Port of the app reference implementation (EspotoProject:
 * SharedCode/src/commonMain/kotlin/system/EspotoKeywords.kt). The replacement is a
 * pure string transformation; all game state it needs comes through a context object
 * (see buildKeywordContext) so the engine is testable without the Vue store.
 *
 * Deliberate webapp deviations from the Kotlin reference (keywords.md section 5):
 * - show_teamname ignores ID:/NR: and always resolves to the own team name — no
 *   cross-team data in keywords (design decision, keywords.md section 9).
 * - show_username resolves to the team name: the webapp does not query the account
 *   name endpoint; the team name is the intended fallback (see buildKeywordContext).
 * - No base64 media extraction: that was an iOS-only workaround the parser no longer
 *   needs anyway.
 *
 * No regex lookbehind anywhere — the target audience includes older iOS Safari.
 */

const KEYWORD_TOKEN = '[show_';
const CONTENT_MARKER = 'CONTENT:';
const ELSE_MARKER = 'ELSE:';

/**
 * Defensive cap for the CONTENT/ELSE recursion. Termination is guaranteed anyway
 * (the content shrinks on every level), but a hard cap keeps pathologically deep
 * nesting from exhausting the stack.
 */
const MAX_KEYWORD_NESTING = 10;

const KEYWORD_NAMES = [
    'show_teamname',
    'show_username',
    'show_scored_points',
    'show_open_points',
    'show_open_tasks',
    'show_played_tasks',
    'show_solved_tasks',
    'show_failed_tasks',
    'show_event_time',
    'show_team_photo',
    'show_task_solution',
];

const MINUTES_PER_HOUR = 60;
const MINUTES_PER_DAY = 24 * MINUTES_PER_HOUR;

// same markup the CMS text editor (EspotoCms EspTextEditor.vue) inserts for media
const IMAGE_STYLE = 'style="max-width:100%;"';
const VIDEO_STYLE = 'style="width: 98%"';
const VIDEO_FALLBACK = 'Your browser does not support the video tag.';

// solution types of the task list (raetsel.ralsgtyp), values as CONFIG.SOLUTION_TYPE_*_ID
// in src/constants/config.js
const SOLUTION_TYPE_TEXT = 0;
const SOLUTION_TYPE_MC = 1;
const SOLUTION_TYPE_PHOTO = 2;
const SOLUTION_TYPE_QR = 3;
const SOLUTION_TYPE_OPEN = 4;
const SOLUTION_TYPE_VIDEO = 5;
const SOLUTION_TYPE_OPEN_MC = 6;

const WHITESPACE_REGEX = /\s+/;
const WHITESPACE_CHAR_REGEX = /\s/;
// strict integer check like Kotlin's toIntOrNull ("12abc" must not parse as 12)
const INTEGER_REGEX = /^-?\d+$/;

/**
 * Balanced-bracket parser for the espoto keywords — replacement for the former regex
 * implementation. Scans for "[show_" tokens (plain text and unknown [...] brackets pass
 * through untouched), finds the matching closing bracket by counting every '[' up and
 * every ']' down — so balanced foreign pairs like [Zitat] and nested keywords survive
 * inside CONTENT — and evaluates the keyword against the context. CONTENT/ELSE
 * branches are substituted recursively (nesting acts as AND). A lone unbalanced '['
 * inside CONTENT leaves the whole call raw; authors escape lone brackets as
 * &#91; / &#93;.
 */
export function substituteEspotoKeywords(html, context, depth) {
    depth = depth || 0;
    if (depth >= MAX_KEYWORD_NESTING) {
        return html;
    }
    if (html.indexOf(KEYWORD_TOKEN) === -1) {
        return html;
    }

    let out = '';
    let i = 0;
    while (i < html.length) {
        const open = html.indexOf(KEYWORD_TOKEN, i);
        if (open === -1) {
            out += html.substring(i);
            break;
        }
        out += html.substring(i, open);

        const close = findClosingBracket(html, open);
        if (close === -1) {
            // no matching closer: pass the defective call through visibly raw
            out += html.substring(open);
            break;
        }

        const body = html.substring(open + 1, close);
        const nameEnd = body.search(WHITESPACE_CHAR_REGEX);
        const name = nameEnd === -1 ? body : body.substring(0, nameEnd);
        if (KEYWORD_NAMES.indexOf(name) === -1) {
            // not one of the eight keywords (e.g. a typo like show_teamnameXX): emit the
            // bracket and rescan right behind it so keywords further inside are still found
            out += '[';
            i = open + 1;
            continue;
        }

        const argsRaw = nameEnd === -1 ? '' : body.substring(nameEnd + 1);
        const replacement = evaluateKeyword(name, argsRaw, context, depth);
        if (replacement !== null) {
            out += replacement;
        } else {
            out += html.substring(open, close + 1);
        }
        i = close + 1;
    }
    return out;
}

/**
 * Index of the ']' closing the bracket at open, counting every '[' up and every ']'
 * down; -1 when the brackets never balance out.
 */
function findClosingBracket(text, open) {
    let depth = 1;
    let i = open + 1;
    while (i < text.length) {
        const c = text.charAt(i);
        if (c === '[') {
            depth++;
        } else if (c === ']') {
            depth--;
            if (depth === 0) {
                return i;
            }
        }
        i++;
    }
    return -1;
}

/**
 * Splits the argument part of a keyword call. CONTENT: and ELSE: are recognized only
 * at bracket depth 0 and only at a token boundary, so markers inside nested keywords
 * or glued to other text do not split the outer call. Everything after CONTENT: up to
 * a depth-0 ELSE: (or the end) is the verbatim then-branch — the single whitespace
 * separating it from ELSE: belongs to the marker, not to the branch. Everything after
 * ELSE: is the verbatim else-branch. The head before the first marker is parsed as
 * KEY:value tokens; the first parseable value per key wins.
 */
function parseArgs(argsRaw) {
    let contentIdx = -1;
    let elseIdx = -1;
    let depth = 0;
    let i = 0;
    while (i < argsRaw.length) {
        const c = argsRaw.charAt(i);
        if (c === '[') {
            depth++;
        } else if (c === ']') {
            if (depth > 0) depth--;
        } else if (depth === 0 && (i === 0 || WHITESPACE_CHAR_REGEX.test(argsRaw.charAt(i - 1)))) {
            if (contentIdx === -1 && argsRaw.startsWith(CONTENT_MARKER, i)) {
                contentIdx = i;
                i += CONTENT_MARKER.length;
                continue;
            }
            if (elseIdx === -1 && argsRaw.startsWith(ELSE_MARKER, i)) {
                elseIdx = i;
                i += ELSE_MARKER.length;
                continue;
            }
        }
        i++;
    }

    let content = null;
    let elseContent = null;
    let firstMarker = -1;
    if (contentIdx !== -1 && (elseIdx === -1 || contentIdx < elseIdx)) {
        firstMarker = contentIdx;
        if (elseIdx === -1) {
            content = argsRaw.substring(contentIdx + CONTENT_MARKER.length);
        } else {
            // elseIdx - 1 is the whitespace that separates the branch from ELSE:
            content = argsRaw.substring(contentIdx + CONTENT_MARKER.length, elseIdx - 1);
            elseContent = argsRaw.substring(elseIdx + ELSE_MARKER.length);
        }
    } else if (elseIdx !== -1) {
        firstMarker = elseIdx;
        elseContent = argsRaw.substring(elseIdx + ELSE_MARKER.length);
    }

    const head = firstMarker === -1 ? argsRaw : argsRaw.substring(0, firstMarker);
    let id = null;
    let nr = null;
    let min = null;
    let max = null;
    const tokens = head.split(WHITESPACE_REGEX);
    for (let t = 0; t < tokens.length; t++) {
        const token = tokens[t];
        if (token.startsWith('ID:')) {
            if (id === null) id = parseIntStrict(token.substring(3));
        } else if (token.startsWith('NR:')) {
            if (nr === null) nr = parseIntStrict(token.substring(3));
        } else if (token.startsWith('MIN:')) {
            if (min === null) min = parseIntStrict(token.substring(4));
        } else if (token.startsWith('MAX:')) {
            if (max === null) max = parseIntStrict(token.substring(4));
        }
    }
    return { id: id, nr: nr, min: min, max: max, content: content, elseContent: elseContent };
}

function parseIntStrict(value) {
    return INTEGER_REGEX.test(value) ? parseInt(value, 10) : null;
}

/**
 * Evaluates one keyword call. Returns the replacement text, or null when the call
 * must stay raw (show_task_solution without a spot reference; show_teamname NR: stays
 * raw only in the Kotlin reference, the webapp has no cross-team names).
 */
function evaluateKeyword(name, argsRaw, context, depth) {
    const args = parseArgs(argsRaw);

    let replaceString = '';
    let replaceValue = null;
    // shown instead of replaceValue when the number only serves the MIN/MAX gate
    let displayOverride = null;
    let conditionMet = true;
    // combat spot: the condition is not evaluable, so neither branch may show
    let suppressElse = false;

    if (name === 'show_event_time') {
        const minutes = context.remainingEventMinutes;
        if (minutes === null || minutes === undefined) {
            return '';
        }
        replaceValue = minutes;
        displayOverride = formatRemainingTime(minutes, context);
    } else if (name === 'show_team_photo') {
        const url = context.teamPhotoUrl;
        if (url) {
            replaceString = renderImage(url);
        } else {
            conditionMet = false;
        }
    } else if (name === 'show_task_solution') {
        if (args.id === null && args.nr === null) {
            // missing spot reference: an authoring error, keep it visible like an unknown keyword
            return null;
        }
        const answer = context.taskAnswer(args.id, args.nr);
        if (answer.kind === 'notFound') {
            conditionMet = false;
            suppressElse = true;
        } else if (answer.kind === 'notAnswered' || answer.kind === 'unsupported') {
            conditionMet = false;
        } else {
            replaceString = renderTaskAnswer(answer);
        }
    } else if (name === 'show_teamname') {
        // deliberate webapp deviation: ID:/NR: (cross-team names in the app) are
        // ignored — always the own team name (keywords.md sections 5 and 9)
        replaceString = context.teamName;
    } else if (name === 'show_username') {
        replaceString = context.userName;
    } else if (name === 'show_scored_points') {
        replaceValue = context.points;
    } else if (name === 'show_open_points') {
        replaceValue = context.openPoints;
    } else if (args.id !== null || args.nr !== null) {
        const lookup = context.spotLookup(args.id, args.nr);
        if (lookup) {
            if (name === 'show_open_tasks') {
                conditionMet = !lookup.played;
            } else if (name === 'show_played_tasks') {
                conditionMet = lookup.played;
            } else if (name === 'show_solved_tasks') {
                conditionMet = lookup.solved;
            } else {
                conditionMet = lookup.failed;
            }
        } else if (name === 'show_failed_tasks') {
            conditionMet = true; // combat spot, replace anyway
        } else {
            conditionMet = false;
            suppressElse = true;
        }
    } else if (name === 'show_open_tasks') {
        replaceValue = context.openTasks;
    } else if (name === 'show_played_tasks') {
        replaceValue = context.solvedTasks + context.failedTasks;
    } else if (name === 'show_solved_tasks') {
        replaceValue = context.solvedTasks;
    } else {
        replaceValue = context.failedTasks;
    }

    // MIN/MAX gate only applies to numeric replacement values, never to ID:/NR: conditions
    if (replaceValue !== null) {
        if (args.min !== null && replaceValue < args.min) conditionMet = false;
        if (args.max !== null && replaceValue > args.max) conditionMet = false;
    }

    if (conditionMet) {
        if (args.content !== null) {
            return substituteEspotoKeywords(args.content, context, depth + 1);
        }
        if (replaceValue !== null) {
            return displayOverride !== null ? displayOverride : String(replaceValue);
        }
        return replaceString;
    }
    if (!suppressElse && args.elseContent !== null) {
        return substituteEspotoKeywords(args.elseContent, context, depth + 1);
    }
    return '';
}

/** Largest unit that fits into minutes with its rounded-down count (0..59 -> minutes). */
function largestTimeUnit(minutes) {
    if (minutes >= MINUTES_PER_DAY) {
        return { unit: 'days', count: Math.floor(minutes / MINUTES_PER_DAY) };
    }
    if (minutes >= MINUTES_PER_HOUR) {
        return { unit: 'hours', count: Math.floor(minutes / MINUTES_PER_HOUR) };
    }
    return { unit: 'minutes', count: minutes };
}

function formatRemainingTime(minutes, context) {
    const largest = largestTimeUnit(minutes);
    return largest.count + ' ' + context.timeUnitLabel(largest.unit, largest.count);
}

function renderImage(url) {
    return '<img src="' + url + '" ' + IMAGE_STYLE + '>';
}

function renderVideo(url) {
    return '<p style="text-align:center"><video controls ' + VIDEO_STYLE + '><source src="' + url + '">' +
        VIDEO_FALLBACK + '</video></p>';
}

function renderTaskAnswer(answer) {
    switch (answer.kind) {
        case 'text':
            return escapeHtml(answer.value);
        case 'options':
            return '<ul>' + answer.items.map((item) => '<li>' + escapeHtml(item) + '</li>').join('') + '</ul>';
        case 'image':
            return renderImage(answer.url);
        case 'video':
            return renderVideo(answer.url);
        default:
            return '';
    }
}

function escapeHtml(text) {
    return String(text)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

/**
 * Builds the keyword context from the webapp's offline statistic (see
 * Utils.getOfflineStatistic in store/modules/base.js), the flat spot list and the
 * extras of eventMixin.keywordExtras (remaining event time, team photo, media base
 * data for solution photos/videos). Counterpart of the app's DefaultKeywordContext,
 * bound to the webapp data model: spot status via tstatus (3 = solved, 4 = failed),
 * NR: via the spot column waintnr, answers via raetsel.user_input / ralsgtyp.
 * Before the spot list is loaded the statistic can still be empty — counters then
 * default to 0 and names to '' so keywords never render as "undefined". Without
 * extras the time and photo keywords render empty and answers count as not found.
 */
export function buildKeywordContext(myStatistic, spots, extras) {
    const ext = extras || {};
    // deliberate webapp deviation: show_username also resolves to the team name —
    // the account name endpoint is not queried, the team name is the intended
    // fallback instead of showing nothing (keywords.md section 5)
    const teamName = myStatistic.teamName != null ? String(myStatistic.teamName) : '';
    const remaining = ext.remainingEventMinutes;
    return {
        teamName: teamName,
        userName: teamName,
        points: toCount(myStatistic.points),
        openPoints: toCount(myStatistic.openPoints),
        solvedTasks: toCount(myStatistic.correct),
        failedTasks: toCount(myStatistic.failed),
        openTasks: toCount(myStatistic.openTasks),
        remainingEventMinutes: typeof remaining === 'number' && isFinite(remaining) ? Math.max(0, Math.floor(remaining)) : null,
        timeUnitLabel: typeof ext.timeUnitLabel === 'function' ? ext.timeUnitLabel : (unit) => unit,
        teamPhotoUrl: typeof ext.teamPhotoUrl === 'string' && ext.teamPhotoUrl !== '' ? ext.teamPhotoUrl : null,
        spotLookup: function (id, nr) {
            const spot = findSpotByIdOrNr(spots, id, nr);
            if (!spot) {
                return null;
            }
            const solved = spot.tstatus === 3;
            const failed = spot.tstatus === 4;
            return { played: solved || failed, solved: solved, failed: failed };
        },
        taskAnswer: function (id, nr) {
            const spot = extras ? findSpotByIdOrNr(spots, id, nr) : null;
            return spot ? answerOfSpot(spot, ext) : { kind: 'notFound' };
        },
    };
}

function toCount(value) {
    return typeof value === 'number' && isFinite(value) ? value : 0;
}

/**
 * Reduces the stored answer of a spot (last trial, as delivered in raetsel.user_input)
 * to what the engine renders — same mapping as DefaultKeywordContext.taskAnswer in the
 * app: text-like types -> text, MC types -> options, photo/video -> media URL, voting,
 * AR and jsTask -> unsupported.
 */
function answerOfSpot(spot, ext) {
    const raetsel = spot.raetsel;
    if (!raetsel) {
        return { kind: 'notAnswered' };
    }
    const type = parseInt(raetsel.ralsgtyp, 10);
    const input = toStringList(raetsel.user_input);
    switch (type) {
        case SOLUTION_TYPE_TEXT:
        case SOLUTION_TYPE_QR:
        case SOLUTION_TYPE_OPEN:
            return input.length > 0 ? { kind: 'text', value: input[0] } : { kind: 'notAnswered' };
        case SOLUTION_TYPE_MC:
        case SOLUTION_TYPE_OPEN_MC:
            return input.length > 0 ? { kind: 'options', items: input } : { kind: 'notAnswered' };
        case SOLUTION_TYPE_PHOTO:
        case SOLUTION_TYPE_VIDEO:
            if (input.length === 0) {
                return { kind: 'notAnswered' };
            }
            return { kind: type === SOLUTION_TYPE_PHOTO ? 'image' : 'video', url: solutionMediaUrl(input[0], ext) };
        default:
            return { kind: 'unsupported' };
    }
}

function toStringList(value) {
    const list = Array.isArray(value) ? value : (value == null ? [] : [value]);
    return list.filter((item) => typeof item === 'string' && item.trim() !== '');
}

/**
 * Absolute URL of a solution photo/video. Server-delivered inputs carry the folder
 * (/fotos/event_<eventId>_teamId_<teamId>/file), inputs stored after a webapp solve are
 * bare file names — same rule as EspSolutionTypeMedia.avatarUrl.
 */
function solutionMediaUrl(input, ext) {
    const base = typeof ext.mediaBaseUrl === 'string' ? ext.mediaBaseUrl : '';
    const path = input.replace(/^\/+/, '');
    if (path.indexOf('fotos/event_') > -1) {
        return base + path;
    }
    return base + 'fotos/event_' + ext.eventId + '_teamId_' + ext.teamId + '/' + path;
}

/** Spot referenced by ID: or NR: (ID wins when both are present); null = not found. */
function findSpotByIdOrNr(spots, id, nr) {
    if (!spots || spots.length === 0) {
        return null;
    }
    for (let i = 0; i < spots.length; i++) {
        const spot = spots[i];
        if (!spot) continue;
        if (id !== null) {
            if (spot.id === id) return spot;
        } else if (parseInt(spot.waintnr, 10) === nr) {
            return spot;
        }
    }
    return null;
}
